import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getMyFastrackNodes } from '../../api/fastrackApi';
import { getPlansWithStatus } from '../../api/planApi';
import { ActiveNodeCard } from '../../components/fastrack/ActiveNodeCard';

const cyan = '#00e5ff';
const accent = '#cc66ff';
const orange = '#ffaa44';
const cardDark = { background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0033 100%)', color: '#fff', border: '1px solid #9933ff44' };
const placeholderStyle = { ...cardDark, minHeight: 200 };

const FastrackStatus = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [fastrackByPlan, setFastrackByPlan] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Per-slot selected renewal tab (planId → renewalIdx)
  const [selectedRenewalByPlan, setSelectedRenewalByPlan] = useState({});

  useEffect(() => {
    Promise.all([getPlansWithStatus(), getMyFastrackNodes()])
      .then(([plansRes, fastrackRes]) => {
        setPlans(plansRes.data.plans || []);
        const map = {};
        (fastrackRes.data.plans || []).forEach((pd) => {
          map[pd.plan._id] = pd;
        });
        setFastrackByPlan(map);
        setSummary(fastrackRes.data.summary || null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: accent }} />
      </Container>
    );
  }

  const purchasedPlans = plans.filter((p) => p.purchased);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <button
          onClick={() => navigate('/account-status')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Account Status
        </button>

        <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 16 }}>Fastrack Status</h4>

        {/* Global summary */}
        {summary && (
          <Row className="g-3 mb-4">
            <Col xs={6} md={4}>
              <Card style={{ ...cardDark, borderColor: '#ffaa4444' }} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: orange }}>{summary.totalHeld.toFixed(2)}</div>
                <div style={{ color: '#aaa', fontSize: 12 }}>On Hold (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={4}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: accent }}>{summary.totalCompleted}</div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Cycles Completed</div>
              </Card>
            </Col>
            <Col xs={6} md={4}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: '#66aaff' }}>{summary.totalActive}</div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Active Nodes</div>
              </Card>
            </Col>
          </Row>
        )}

        {purchasedPlans.length === 0 && (
          <div style={{ color: '#555', textAlign: 'center', padding: 40, fontSize: 13 }}>
            No slots purchased yet.
          </div>
        )}

        {/* Per-slot fastrack cards with renewal tabs */}
        {purchasedPlans.map((plan) => {
          const fastrackData = fastrackByPlan[plan._id];
          const renewals = fastrackData?.renewals || [];

          // If no fastrack data yet, show placeholder
          if (!fastrackData || renewals.length === 0) {
            return (
              <div key={plan._id} style={{ marginBottom: 28 }}>
                <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{plan.planName}</div>
                <Card style={placeholderStyle} className="p-3">
                  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 140 }}>
                    <div style={{ color: '#555', fontSize: 12 }}>No active node yet</div>
                  </div>
                </Card>
              </div>
            );
          }

          const selectedIdx = selectedRenewalByPlan[plan._id] ?? 0;
          const safeIdx = Math.min(selectedIdx, renewals.length - 1);
          const renewal = renewals[safeIdx];

          const activeNode = renewal?.nodes?.find((n) => n.status === 'active');
          const cyclesCompleted = renewal?.completedCycles ?? 0;
          const totalEarned = (renewal?.nodes || [])
            .filter((n) => n.bonusStatus === 'credited')
            .reduce((sum, n) => sum + (n.bonusAmount || 0), 0);

          const statsRow = (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(153,51,255,0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
                <span style={{ color: accent, fontWeight: 700, fontSize: 14 }}>{cyclesCompleted}</span>
              </div>
              <div style={{ color: '#66ff99', fontWeight: 700, fontSize: 14 }}>
                {totalEarned.toFixed(2)} <span style={{ fontSize: 10 }}>USDT</span>
              </div>
            </div>
          );

          return (
            <div key={plan._id} style={{ marginBottom: 32 }}>
              {/* Slot header */}
              <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{plan.planName}</div>

              {/* Renewal tabs */}
              {renewals.length > 1 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                  {renewals.map((r, ri) => (
                    <button
                      key={r.renewalLabel}
                      onClick={() => setSelectedRenewalByPlan((prev) => ({ ...prev, [plan._id]: ri }))}
                      style={{
                        padding: '4px 14px',
                        borderRadius: 16,
                        border: safeIdx === ri
                          ? `1px solid ${r.renewalCount === 0 ? cyan : orange}`
                          : '1px solid rgba(255,255,255,0.1)',
                        background: safeIdx === ri
                          ? r.renewalCount === 0 ? 'rgba(0,229,255,0.1)' : 'rgba(255,170,68,0.1)'
                          : 'transparent',
                        color: safeIdx === ri
                          ? r.renewalCount === 0 ? cyan : orange
                          : '#555',
                        fontWeight: 600,
                        fontSize: 11,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {r.renewalLabel}
                      {r.heldAmount > 0 && (
                        <span style={{ marginLeft: 4, color: orange, fontSize: 9 }}>● held</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Active node card for the selected renewal */}
              <Row className="g-3">
                <Col xs={12} md={6} lg={4}>
                  {activeNode ? (
                    <ActiveNodeCard node={{ ...activeNode, planId: plan }} planName={plan.planName} bottomStats={statsRow} />
                  ) : (
                    <Card style={placeholderStyle} className="p-3">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
                        {plan.planName} · {renewal.renewalLabel}
                      </div>
                      <div className="d-flex align-items-center justify-content-center text-center" style={{ minHeight: 140 }}>
                        <div style={{ color: '#555', fontSize: 12 }}>No active node right now</div>
                      </div>
                      {statsRow}
                    </Card>
                  )}
                </Col>

                {/* Held/condition info pill */}
                {renewal.heldAmount > 0 && (
                  <Col xs={12} md={6} lg={4}>
                    <Card style={{ ...cardDark, borderColor: `${orange}44` }} className="p-3 h-100">
                      <div style={{ color: orange, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>On Hold</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: orange }}>{renewal.heldAmount.toFixed(2)} USDT</div>
                      <div style={{ color: '#555', fontSize: 11, marginTop: 8 }}>
                        Batch {renewal.currentBatch} requires {renewal.currentBatch - 1} direct referral{renewal.currentBatch - 1 !== 1 ? 's' : ''} with this slot.
                        You have {renewal.directSlotCount}.
                      </div>
                    </Card>
                  </Col>
                )}
              </Row>
            </div>
          );
        })}
      </Container>
    </div>
  );
};

export default FastrackStatus;
